import { DropDownTreeChangeEvent, ItemProps } from '@progress/kendo-react-dropdowns';
import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import TreeDropDown from 'src/components/design-system/DropDownTree';
import { ClosedTag } from 'src/sections/view_checkbook/PeriodDropdown';
import { GlPeriodObject } from 'src/services/useGLPeriodApi';
import { GLPeriodDropdownChildren } from 'src/utils/transformation';
import { processTreeData } from 'src/utils/treeDataOperations';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';

const selectField = 'selected';
const expandField = 'expanded';
const dataItemKey = 'id';
const textField = 'label';
const subItemsField = 'items';
const fields = { selectField, expandField, dataItemKey, subItemsField };

const BudgetPeriodDropdown = () => {
  const dropDownValues = useContextSelector(InvoiceContext, (value) => value.dropDownValues);
  const glPeriods = useContextSelector(InvoiceContext, (value) => value.glPeriods);
  const setSingleGlPeriodStartDate = useContextSelector(
    InvoiceContext,
    (value) => value.setSingleGlPeriodStartDate
  );
  const setInvoiceBudgetPeriods = useContextSelector(
    InvoiceContext,
    (value) => value.setInvoiceBudgetPeriods
  );
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const handleInputChange = useContextSelector(InvoiceContext, (value) => value.handleInputChange);

  const value = invoice.glPeriodId;
  const [expanded, setExpanded] = useState([] as number[]);
  const [dropDownOpened, setDropDownOpened] = useState<boolean>(false);

  const findBudgetPeriod = glPeriods.find((item) => item.glPeriodId === value) as GlPeriodObject;

  const dropDownValue = useMemo(() => {
    const currentGlPeriods = dropDownValues.find(
      (value) => value.fiscalYear === findBudgetPeriod?.fiscalYear
    )?.items as GLPeriodDropdownChildren[];
    return (
      currentGlPeriods?.find((period) => period.glPeriodId === value) ??
      ({} as GLPeriodDropdownChildren)
    );
  }, [dropDownValues, findBudgetPeriod?.fiscalYear, value]);

  const initialExpandedPeriod = useCallback(() => {
    const initialExpand = dropDownValue
      ? [dropDownValue?.fiscalYear]
      : !isEmpty(dropDownValues)
      ? [dropDownValues?.[1]?.fiscalYear]
      : [];
    setExpanded(initialExpand);
  }, [dropDownValue, dropDownValues]);

  const handleChange = useCallback(
    (event: DropDownTreeChangeEvent) => {
      const { level = [], value = {} } = event;
      const { fiscalYear = 0, glPeriodId, startDate, isClosed } = value || {};
      if (isClosed) {
        return;
      }
      if (isEmpty(level) || level.length > 1) {
        setInvoiceBudgetPeriods([value]);
        setDropDownOpened(false);
        const onChange = handleInputChange('glPeriodId');
        onChange({ value: glPeriodId } as TextBoxChangeEvent);
        setSingleGlPeriodStartDate(startDate);
      } else if (level.length === 1)
        setExpanded((prev) => {
          if (prev.includes(fiscalYear)) {
            return prev.filter((item) => item !== fiscalYear);
          } else {
            return [...prev, fiscalYear];
          }
        });
    },
    [handleInputChange, setInvoiceBudgetPeriods, setSingleGlPeriodStartDate]
  );

  const Item = useCallback(
    (props: ItemProps) => {
      const { label, content, parent = false, glPeriodId, isClosed } = props.item;
      const isSelected = glPeriodId === value;

      return (
        <>
          {parent ? (
            <span onClick={() => setDropDownOpened(true)}>{content}</span>
          ) : isSelected ? (
            <span id="invoice-budget-period-dropdown-selected-item">
              {label} {isClosed === 1 && <ClosedTag selected />}
            </span>
          ) : (
            <>
              <span
                className={`cursor-default hover:cursor-pointer ${
                  isClosed === 1 ? 'text-[#d0d0d0] !cursor-not-allowed' : ''
                }`}
              >
                {label} {isClosed === 1 && <ClosedTag />}
              </span>
            </>
          )}
        </>
      );
    },
    [value]
  );

  const treeData = useMemo(
    () => processTreeData(dropDownValues, { expanded, dropDownValue }, fields),
    [dropDownValue, dropDownValues, expanded]
  );
  useEffect(() => {
    if (dropDownOpened) {
      initialExpandedPeriod();
      setTimeout(() => {
        const element = document.getElementById('invoice-budget-period-dropdown-selected-item');
        element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 200);
    }
  }, [dropDownOpened, initialExpandedPeriod]);

  return (
    <div title={!isEmpty(dropDownValue) ? `${dropDownValue?.label}` : ''}>
      <TreeDropDown
        key={value}
        opened
        value={dropDownValue}
        placeholder="Select accounting period"
        data={treeData}
        onChange={handleChange}
        expanded={expanded}
        size={'small'}
        id="period-dropdown"
        className="checkbook-period-dropdown"
        setExpanded={setExpanded}
        dropDownOpened={dropDownOpened}
        setDropDownOpened={setDropDownOpened}
        textField={textField}
        dataItemKey={dataItemKey}
        selectField={selectField}
        expandField={expandField}
        subItemsField={subItemsField}
        item={Item}
        popupSettings={{
          popupClass: 'max-h-[45vh]',
          className: 'border-gray-500 border-opacity-10 padding-bottom-treeview',
        }}
        setInitialExpandValue={initialExpandedPeriod}
      />
    </div>
  );
};

export default BudgetPeriodDropdown;
